<template>
  <HrPageLayout
    title="Performance Appraisal Reports & Analytics"
    subtitle="Reports"
    :breadcrumb-items="[
      { label: 'HR Dashboard', route: '/hr-dashboard' },
      { label: 'Reports' }
    ]"
  >
    <div class="reports-analytics-content">
      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid hr-align-end" style="grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem;">
            <div>
              <label class="hr-form-label">Select Year</label>
              <select v-model="filters.year" @change="loadReports" class="hr-select">
                <option value="">All Years</option>
                <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div>
              <label class="hr-form-label">Department</label>
              <select v-model="filters.department" @change="loadReports" class="hr-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
              </select>
            </div>
            <div>
              <label class="hr-form-label">Status</label>
              <select v-model="filters.status" @change="loadReports" class="hr-select">
                <option value="">All Status</option>
                <option value="Draft">Draft</option>
                <option value="Self-Assessment Complete">Self-Assessment Complete</option>
                <option value="Appraiser Review Complete">Appraiser Review Complete</option>
                <option value="Approved">Approved</option>
              </select>
            </div>
            <div class="hr-flex hr-flex-col hr-gap-2">
              <button @click="exportReport" class="hr-btn hr-btn-success hr-w-100" :disabled="exporting">
                <i class="bx bx-file-blank"></i>
                {{ exporting ? 'Exporting...' : 'Export CSV' }}
              </button>
              <button @click="exportToExcel" class="hr-btn hr-btn-outline-success hr-w-100" :disabled="exporting">
                <i class="bx bxl-microsoft"></i>
                Export Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p>Loading reports...</p>
      </div>

      <!-- Reports Content -->
      <div v-else>
        <!-- Summary Statistics -->
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Total Appraisals</p>
            <h3 class="hr-metric-value">{{ statistics.total }}</h3>
            </div>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <p class="hr-metric-label">Completed</p>
            <h3 class="hr-metric-value hr-text-success">{{ statistics.completed }}</h3>
            <p class="hr-metric-subtext">{{ statistics.completedPercent }}%</p>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <p class="hr-metric-label">Pending</p>
            <h3 class="hr-metric-value hr-text-warning">{{ statistics.pending }}</h3>
            <p class="hr-metric-subtext">{{ statistics.pendingPercent }}%</p>
          </div>
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-star"></i></div>
            <p class="hr-metric-label">Average Rating</p>
            <h3 class="hr-metric-value hr-text-info">{{ statistics.averageRating }}</h3>
            <p class="hr-metric-subtext">out of 5.0</p>
          </div>
        </div>

        <!-- Charts Row -->
        <div class="hr-content-grid hr-mb-4" style="grid-template-columns: repeat(2, 1fr);">
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title">Department-wise Distribution</h5>
            </div>
            <div class="hr-chart-body">
              <canvas id="departmentChart" height="300"></canvas>
            </div>
          </div>
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title">Status Distribution</h5>
            </div>
            <div class="hr-chart-body">
              <canvas id="statusChart" height="300"></canvas>
            </div>
          </div>
        </div>

        <!-- Rating Distribution Chart -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title">Rating Distribution</h5>
          </div>
          <div class="hr-chart-body">
            <canvas id="ratingChart" height="100"></canvas>
          </div>
        </div>

        <!-- Department Performance Table -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-header hr-flex hr-justify-between hr-align-center">
            <h5 class="hr-chart-title">Department Performance Summary</h5>
            <button @click="exportDepartmentReport" class="hr-btn hr-btn-sm hr-btn-outline-success">
              <i class="bx bx-download"></i> Export
            </button>
          </div>
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-bordered hr-table-hover">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th class="hr-text-center">Total</th>
                    <th class="hr-text-center">Completed</th>
                    <th class="hr-text-center">Pending</th>
                    <th class="hr-text-center">Avg Rating</th>
                    <th class="hr-text-center">Completion %</th>
                    <th class="hr-text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="dept in departmentPerformance" :key="dept.department">
                    <td><strong>{{ dept.department }}</strong></td>
                    <td class="hr-text-center">{{ dept.total }}</td>
                    <td class="hr-text-center">
                      <span class="hr-badge hr-badge-success">{{ dept.completed }}</span>
                    </td>
                    <td class="hr-text-center">
                      <span class="hr-badge hr-badge-warning">{{ dept.pending }}</span>
                    </td>
                    <td class="hr-text-center"><strong class="hr-text-info">{{ dept.avgRating }}</strong></td>
                    <td class="hr-text-center">
                      <div class="hr-progress" style="height: 20px;">
                        <div class="hr-progress-bar hr-bg-success" :style="{ width: dept.completionPercent + '%' }">
                          {{ dept.completionPercent }}%
                        </div>
                      </div>
                    </td>
                    <td class="hr-text-center">
                      <button @click="viewDepartmentDetails(dept.department)" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                        <i class="bx bx-show"></i> View
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Top Performers + Rating by Range -->
        <div class="hr-content-grid hr-mb-4" style="grid-template-columns: repeat(2, 1fr);">
          <div class="hr-chart-card">
            <div class="hr-chart-header" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: #fff; border: none;">
              <h5 class="hr-chart-title" style="color: #fff;">
                <i class="bx bx-trophy"></i> Top Performers
              </h5>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-sm">
                  <thead>
                    <tr>
                      <th width="10%">#</th>
                      <th width="50%">Employee</th>
                      <th width="25%">Department</th>
                      <th width="15%" class="hr-text-center">Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(performer, index) in topPerformers" :key="performer.id">
                      <td>
                        <span v-if="index === 0" class="hr-badge hr-badge-warning">🥇</span>
                        <span v-else-if="index === 1" class="hr-badge hr-badge-secondary">🥈</span>
                        <span v-else-if="index === 2" class="hr-badge hr-badge-danger">🥉</span>
                        <span v-else>{{ index + 1 }}</span>
                      </td>
                      <td>{{ performer.employeeName }}</td>
                      <td>{{ performer.department }}</td>
                      <td class="hr-text-center"><span class="hr-badge hr-badge-success">{{ performer.finalRating }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-bar-chart-alt"></i> Rating Distribution by Range</h5>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-sm">
                  <thead>
                    <tr>
                      <th>Rating Range</th>
                      <th class="hr-text-center">Count</th>
                      <th class="hr-text-center">Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><span class="hr-badge hr-badge-success">4.5 - 5.0 (Outstanding)</span></td>
                      <td class="hr-text-center">{{ ratingDistribution.outstanding }}</td>
                      <td class="hr-text-center">{{ ratingDistribution.outstandingPercent }}%</td>
                    </tr>
                    <tr>
                      <td><span class="hr-badge hr-badge-info">4.0 - 4.4 (Excellent)</span></td>
                      <td class="hr-text-center">{{ ratingDistribution.excellent }}</td>
                      <td class="hr-text-center">{{ ratingDistribution.excellentPercent }}%</td>
                    </tr>
                    <tr>
                      <td><span class="hr-badge hr-badge-primary">3.5 - 3.9 (Good)</span></td>
                      <td class="hr-text-center">{{ ratingDistribution.good }}</td>
                      <td class="hr-text-center">{{ ratingDistribution.goodPercent }}%</td>
                    </tr>
                    <tr>
                      <td><span class="hr-badge hr-badge-warning">3.0 - 3.4 (Average)</span></td>
                      <td class="hr-text-center">{{ ratingDistribution.average }}</td>
                      <td class="hr-text-center">{{ ratingDistribution.averagePercent }}%</td>
                    </tr>
                    <tr>
                      <td><span class="hr-badge hr-badge-danger">Below 3.0 (Needs Improvement)</span></td>
                      <td class="hr-text-center">{{ ratingDistribution.needsImprovement }}</td>
                      <td class="hr-text-center">{{ ratingDistribution.needsImprovementPercent }}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Detailed Appraisals List -->
        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex hr-justify-between hr-align-center">
            <h5 class="hr-chart-title">Detailed Appraisal Records</h5>
            <input v-model="searchQuery" type="text" class="hr-input" placeholder="Search employee..." style="width: 250px;">
          </div>
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-bordered hr-table-hover hr-table-sm">
                <thead>
                  <tr>
                    <th width="5%">#</th>
                    <th width="20%">Employee</th>
                    <th width="15%">Department</th>
                    <th width="10%">Year</th>
                    <th width="8%">Self Rating</th>
                    <th width="8%">Manager Rating</th>
                    <th width="8%">Final Rating</th>
                    <th width="12%">Status</th>
                    <th width="10%">Submitted</th>
                    <th width="4%">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(appraisal, index) in filteredAppraisals" :key="appraisal.id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ appraisal.employeeName }}</td>
                    <td>{{ appraisal.department }}</td>
                    <td>{{ appraisal.appraisalYear }}</td>
                    <td class="hr-text-center">
                      <span v-if="appraisal.sectionA_SelfOverallRating" class="hr-badge hr-badge-primary">{{ parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) }}</span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td class="hr-text-center">
                      <span v-if="appraisal.sectionA_AppraiserOverallRating" class="hr-badge hr-badge-info">{{ parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) }}</span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td class="hr-text-center">
                      <span v-if="appraisal.finalOverallRating" class="hr-badge hr-badge-success">{{ parseFloat(appraisal.finalOverallRating).toFixed(2) }}</span>
                      <span v-else class="hr-text-muted">-</span>
                    </td>
                    <td>
                      <span :class="['hr-badge', getStatusBadgeClass(appraisal.status)]">{{ appraisal.status }}</span>
                    </td>
                    <td>{{ formatDate(appraisal.selfAssessmentDate) }}</td>
                    <td class="hr-text-center">
                      <button @click="viewAppraisal(appraisal.id)" class="hr-btn hr-btn-sm hr-btn-outline-primary">
                        <i class="bx bx-show"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="hr-flex hr-justify-between hr-align-center hr-mt-3">
              <span>Showing {{ filteredAppraisals.length }} of {{ allAppraisals.length }} records</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import Chart from 'chart.js/auto';

export default {
  name: "HRReports",

  components: {
    HrPageLayout
  },

  data() {
    return {
      loading: false,
      exporting: false,
      searchQuery: '',

      filters: {
        year: new Date().getFullYear(),
        department: '',
        status: ''
      },

      availableYears: [],
      departments: [],

      statistics: {
        total: 0,
        completed: 0,
        pending: 0,
        completedPercent: 0,
        pendingPercent: 0,
        averageRating: '0.00'
      },

      departmentPerformance: [],
      topPerformers: [],
      ratingDistribution: {
        outstanding: 0,
        excellent: 0,
        good: 0,
        average: 0,
        needsImprovement: 0,
        outstandingPercent: 0,
        excellentPercent: 0,
        goodPercent: 0,
        averagePercent: 0,
        needsImprovementPercent: 0
      },

      allAppraisals: [],

      charts: {
        department: null,
        status: null,
        rating: null
      }
    };
  },

  computed: {
    filteredAppraisals() {
      if (!this.searchQuery) return this.allAppraisals;

      const query = this.searchQuery.toLowerCase();
      return this.allAppraisals.filter(a =>
          a.employeeName.toLowerCase().includes(query) ||
          a.department.toLowerCase().includes(query)
      );
    }
  },

  mounted() {
    this.initializeYears();
    this.loadReports();
  },

  beforeUnmount() {
    // Destroy charts on component unmount
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy();
    });
  },

  methods: {
    initializeYears() {
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 5; i++) {
        this.availableYears.push(currentYear - i);
      }
    },

    async loadReports() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAppraisalReports");
        data.append("year", this.filters.year);
        data.append("department", this.filters.department);
        data.append("status", this.filters.status);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const reports = res.data.data;

          this.statistics = reports.statistics;
          this.departmentPerformance = reports.departmentPerformance;
          this.topPerformers = reports.topPerformers;
          this.ratingDistribution = reports.ratingDistribution;
          this.allAppraisals = reports.allAppraisals;
          this.departments = reports.departments;

          // Wait for DOM update then render charts
          this.$nextTick(() => {
            this.renderCharts();
          });
        }
      } catch (error) {
        console.error("Failed to load reports:", error);
        this.$toast?.error("Error loading reports") || alert("Error loading reports");
      } finally {
        this.loading = false;
      }
    },

    renderCharts() {
      this.renderDepartmentChart();
      this.renderStatusChart();
      this.renderRatingChart();
    },

    renderDepartmentChart() {
      const ctx = document.getElementById('departmentChart');
      if (!ctx) return;

      if (this.charts.department) {
        this.charts.department.destroy();
      }

      const labels = this.departmentPerformance.map(d => d.department);
      const data = this.departmentPerformance.map(d => d.total);

      this.charts.department = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Total Appraisals',
            data: data,
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      });
    },

    renderStatusChart() {
      const ctx = document.getElementById('statusChart');
      if (!ctx) return;

      if (this.charts.status) {
        this.charts.status.destroy();
      }

      this.charts.status = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Completed', 'Pending'],
          datasets: [{
            data: [this.statistics.completed, this.statistics.pending],
            backgroundColor: [
              'rgba(75, 192, 192, 0.7)',
              'rgba(255, 206, 86, 0.7)'
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(255, 206, 86, 1)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom'
            }
          }
        }
      });
    },

    renderRatingChart() {
      const ctx = document.getElementById('ratingChart');
      if (!ctx) return;

      if (this.charts.rating) {
        this.charts.rating.destroy();
      }

      this.charts.rating = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['Outstanding (4.5-5.0)', 'Excellent (4.0-4.4)', 'Good (3.5-3.9)', 'Average (3.0-3.4)', 'Needs Improvement (<3.0)'],
          datasets: [{
            label: 'Number of Employees',
            data: [
              this.ratingDistribution.outstanding,
              this.ratingDistribution.excellent,
              this.ratingDistribution.good,
              this.ratingDistribution.average,
              this.ratingDistribution.needsImprovement
            ],
            backgroundColor: [
              'rgba(75, 192, 192, 0.7)',
              'rgba(54, 162, 235, 0.7)',
              'rgba(153, 102, 255, 0.7)',
              'rgba(255, 206, 86, 0.7)',
              'rgba(255, 99, 132, 0.7)'
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(255, 99, 132, 1)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      });
    },

    async exportReport() {
      this.exporting = true;

      try {
        // Prepare data for export
        const exportData = this.filteredAppraisals.map((appraisal, index) => ({
          '#': index + 1,
          'Employee Name': appraisal.employeeName,
          'Department': appraisal.department,
          'Position': appraisal.positionTitle || '-',
          'Year': appraisal.appraisalYear,
          'Period': `${appraisal.periodStart || ''} to ${appraisal.periodEnd || ''}`,
          'Self Rating (Section A)': appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (Section A)': appraisal.sectionA_AppraiserOverallRating ? parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) : '-',
          'Self Rating (Section B)': appraisal.sectionB_SelfOverallRating ? parseFloat(appraisal.sectionB_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (Section B)': appraisal.sectionB_AppraiserOverallRating ? parseFloat(appraisal.sectionB_AppraiserOverallRating).toFixed(2) : '-',
          'Final Overall Rating': appraisal.finalOverallRating ? parseFloat(appraisal.finalOverallRating).toFixed(2) : '-',
          'Status': appraisal.status,
          'Submitted Date': this.formatDate(appraisal.selfAssessmentDate),
          'Manager Review Date': this.formatDate(appraisal.appraiserReviewDate),
          'HR Review Date': this.formatDate(appraisal.hrCeoReviewDate)
        }));

        // Convert to CSV
        this.downloadCSV(exportData, `Appraisal_Report_${this.filters.year || 'All'}_${new Date().toISOString().split('T')[0]}.csv`);

        this.$toast?.success("Report exported successfully!") || alert("Report exported successfully!");
      } catch (error) {
        console.error("Failed to export report:", error);
        this.$toast?.error("Error exporting report") || alert("Error exporting report");
      } finally {
        this.exporting = false;
      }
    },

    async exportDepartmentReport() {
      try {
        // Prepare department summary for export
        const exportData = this.departmentPerformance.map((dept, index) => ({
          '#': index + 1,
          'Department': dept.department,
          'Total Appraisals': dept.total,
          'Completed': dept.completed,
          'Pending': dept.pending,
          'Average Rating': dept.avgRating,
          'Completion %': dept.completionPercent + '%'
        }));

        // Convert to CSV
        this.downloadCSV(exportData, `Department_Performance_${this.filters.year || 'All'}_${new Date().toISOString().split('T')[0]}.csv`);

        this.$toast?.success("Department report exported!") || alert("Department report exported!");
      } catch (error) {
        console.error("Failed to export department report:", error);
        this.$toast?.error("Error exporting report") || alert("Error exporting report");
      }
    },

    downloadCSV(data, filename) {
      if (!data || data.length === 0) {
        this.$toast?.error("No data to export") || alert("No data to export");
        return;
      }

      // Get headers from first object
      const headers = Object.keys(data[0]);

      // Create CSV content
      let csv = headers.join(',') + '\n';

      // Add data rows
      data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          // Escape values that contain commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return '"' + value.replace(/"/g, '""') + '"';
          }
          return value;
        });
        csv += values.join(',') + '\n';
      });

      // Create blob and download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    },

    exportToExcel() {
      this.exporting = true;

      try {
        // Prepare data for Excel export
        const exportData = this.filteredAppraisals.map((appraisal, index) => ({
          '#': index + 1,
          'Employee Name': appraisal.employeeName,
          'Department': appraisal.department,
          'Position': appraisal.positionTitle || '-',
          'Year': appraisal.appraisalYear,
          'Period Start': appraisal.periodStart || '-',
          'Period End': appraisal.periodEnd || '-',
          'Self Rating (A)': appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (A)': appraisal.sectionA_AppraiserOverallRating ? parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) : '-',
          'Self Rating (B)': appraisal.sectionB_SelfOverallRating ? parseFloat(appraisal.sectionB_SelfOverallRating).toFixed(2) : '-',
          'Manager Rating (B)': appraisal.sectionB_AppraiserOverallRating ? parseFloat(appraisal.sectionB_AppraiserOverallRating).toFixed(2) : '-',
          'Final Rating': appraisal.finalOverallRating ? parseFloat(appraisal.finalOverallRating).toFixed(2) : '-',
          'Status': appraisal.status,
          'Submitted': this.formatDate(appraisal.selfAssessmentDate),
          'Manager Review': this.formatDate(appraisal.appraiserReviewDate),
          'HR Review': this.formatDate(appraisal.hrCeoReviewDate)
        }));

        // Convert to HTML table for Excel
        const headers = Object.keys(exportData[0]);

        let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">';
        html += '<head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>';
        html += '<x:Name>Appraisal Report</x:Name>';
        html += '<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>';
        html += '</x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->';
        html += '<meta charset="UTF-8"></head><body>';
        html += '<table border="1">';

        // Add title row
        html += '<tr><td colspan="' + headers.length + '" style="font-size:16px; font-weight:bold; text-align:center; background-color:#4472C4; color:white;">';
        html += 'Performance Appraisal Report - ' + (this.filters.year || 'All Years');
        if (this.filters.department) html += ' - ' + this.filters.department;
        html += '</td></tr>';

        // Add header row
        html += '<tr style="background-color:#D9E1F2; font-weight:bold;">';
        headers.forEach(header => {
          html += '<td>' + header + '</td>';
        });
        html += '</tr>';

        // Add data rows
        exportData.forEach(row => {
          html += '<tr>';
          headers.forEach(header => {
            html += '<td>' + (row[header] || '') + '</td>';
          });
          html += '</tr>';
        });

        html += '</table></body></html>';

        // Create blob and download
        const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const filename = `Appraisal_Report_${this.filters.year || 'All'}_${new Date().toISOString().split('T')[0]}.xls`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        URL.revokeObjectURL(url);

        this.$toast?.success("Excel report exported successfully!") || alert("Excel report exported successfully!");
      } catch (error) {
        console.error("Failed to export to Excel:", error);
        this.$toast?.error("Error exporting to Excel") || alert("Error exporting to Excel");
      } finally {
        this.exporting = false;
      }
    },

    viewDepartmentDetails(department) {
      this.filters.department = department;
      this.loadReports();
    },

    viewAppraisal(id) {
      this.$router.push({
        path: '/hr-appraisal-view',
        query: { id: id }
      });
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'hr-badge-secondary',
        'Self-Assessment Complete': 'hr-badge-info',
        'Appraiser Review Pending': 'hr-badge-warning',
        'Appraiser Review Complete': 'hr-badge-warning',
        'Reviewer Pending': 'hr-badge-warning',
        'Reviewer Complete': 'hr-badge-primary',
        'HR/CEO Review Pending': 'hr-badge-primary',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger'
      };
      return classes[status] || 'hr-badge-secondary';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-mb-4 { margin-bottom: 1.5rem; }
.hr-mt-3 { margin-top: 1rem; }
.hr-flex-col { flex-direction: column; }
.hr-align-end { align-items: end; }
.hr-w-100 { width: 100%; }

/* Progress bar if not in shared */
.hr-progress {
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.hr-progress-bar {
  height: 100%;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
}
.hr-bg-success { background: #10b981; }

canvas {
  max-height: 300px;
}

@media (max-width: 768px) {
  .hr-content-grid[style*="grid-template-columns: repeat(2, 1fr)"] {
    grid-template-columns: 1fr !important;
  }
}
</style>